import { ArgumentNotSuppliedError } from "./errors";

interface Listener {
	callback: Function;
	thisArg: object | undefined;
}

type EventKind = string | number;

class EventEmitter<TEventKind extends EventKind = EventKind> {

	private readonly _listeners: Map<TEventKind, Listener[]>;

	constructor() {
		this._listeners = new Map<TEventKind, Listener[]>();
	}

	public on(eventKind: TEventKind, callback: Function, thisArg?: object) {
		if (typeof eventKind === "undefined") {
			throw new ArgumentNotSuppliedError("eventKind");
		}
		if (!callback) {
			throw new ArgumentNotSuppliedError("callback");
		}

		const listeners: Listener[] = this._listeners.get(eventKind) || [];

		listeners.push({
			callback,
			thisArg
		});

		this._listeners.set(eventKind, listeners);
	}

	public off(eventKind: TEventKind, callback: Function, thisArg?: object) {
		if (typeof eventKind === "undefined") {
			throw new ArgumentNotSuppliedError("eventKind");
		}
		if (!callback) {
			throw new ArgumentNotSuppliedError("callback");
		}

		if (!this._listeners.has(eventKind)) {
			console.error(`Event '${eventKind}' is not registered`);
			return;
		}

		const listeners: Listener[] = this._listeners.get(eventKind) || [];
		const indexOfListener: number = listeners.findIndex(
			(listener) => listener.callback === callback && listener.thisArg === thisArg
		);
		if (indexOfListener === -1) {
			console.error(`Listener '${callback}' is not registered`);
			return;
		}

		listeners.splice(indexOfListener, 1);
	}

	public emit(eventKind: TEventKind, ...args: any[]) {
		if (typeof eventKind === "undefined") {
			throw new ArgumentNotSuppliedError("eventKind");
		}

		const listeners: Listener[] = this._listeners.get(eventKind) || [];
		if (!listeners || listeners.length === 0) {
			return;
		}

		for (const listener of listeners) {
			listener.callback.apply(listener.thisArg, args);
		}
	}

	public destroy() {
		this._listeners.clear();
	}
}

export {
	EventEmitter
};
